import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * React hook to initialize Ryft Pay SDK.
 *
 * @param {boolean} [disabled=false] - If true, prevents loading the Ryft SDK src element (and all subsequent actions).
 * @param {boolean} [noInit=false] - If true, prevents automatic Ryft.init(). Can be used to defer init until props are loaded/available (e.g. waiting for client secret, for a flow using wallet payments).
 * @param {string|null} [clientSecret=null] - Client secret used for authorization. Can be null and provided at attemptPayment() time to allow for deferred checkout session creation
 *
 * @param {string} [accountId] - Optional sub-account ID.
 * @param {Object} [localisationConfig] - Localisation configuration.
 * @param {Object} [styles] - Style overrides.
 * @param {Object} [applePayConfig] - Apple Pay configuration.
 * @param {Object} [googlePayConfig] - Google Pay configuration.
 * @param {boolean} [customerPaymentMethods.allowStorage=true] - Whether to offer card storage.
 * @param {Object} [customerPaymentMethods.rawJson] - Raw JSON returned from Ryft API.
 * @param {Object} [subscription] - Subscription details.
 * @param {string} [usage='payment'] - Usage context.
 * @param {string} [paymentType='Standard'] - Payment type.
 *
 * @returns {{Ryft: any, ready: boolean}} Ryft SDK object and a ready loading state.
 */
export const useRyftPay = ({
  disabled = false,
  noInit = false,
  clientSecret = null /*  */,
  ...initProps
} = {}) => {
  const ryftRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [initialised, setInitialised] = useState(false);

  const handleScriptLoad = useCallback(() => {
    if (!window?.Ryft || disabled || noInit) {
      return;
    }

    if (!process.env?.REACT_APP_RYFT_PUBLIC_KEY) {
      console.error('REACT_APP_RYFT_PUBLIC_KEY is not specified');
      return;
    }

    if (initialised || ryftRef.current) {
      return; // Don't init twice
    }

    if (!ryftRef.current) {
      ryftRef.current = window.Ryft;
    }

    console.log('INIT Ryft', {
      publicKey: process.env.REACT_APP_RYFT_PUBLIC_KEY,
      clientSecret,
      ...initProps,
    });
    ryftRef.current.init({
      publicKey: process.env.REACT_APP_RYFT_PUBLIC_KEY,
      clientSecret: clientSecret,
      ...initProps,
    });
    setInitialised(true);
  }, [initialised, clientSecret, initProps, disabled, noInit]);

  useEffect(() => {
    if (disabled || ryftRef.current) {
      return;
    }

    const src = 'https://embedded.ryftpay.com/v2/ryft.min.js';
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      console.log('Already Loaded');
      existingScript.addEventListener('load', handleScriptLoad);
      return; // Script already loaded
    }
    if (loaded) {
      handleScriptLoad();
      return; // Loading already in progress
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = handleScriptLoad;
    document.body.appendChild(script);
    setLoaded(true);

    return () => {
      document.body.removeChild(script);
      existingScript?.removeEventListener?.('load', handleScriptLoad);
    };
  }, [disabled, handleScriptLoad, loaded]);

  return { Ryft: ryftRef?.current ?? null, ready: initialised };
};

export default useRyftPay;
